import { memo, useMemo } from "react";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import s from "./ChatAvatars.module.css";

type ChatAvatarsProps = {
  userIds: string[];
  avatarsSize?: {
    oneAvtaar: string;
    multipleAvatars: string;
  };
};

const ChatAvatars = ({
  userIds,
  avatarsSize = { oneAvtaar: "60px", multipleAvatars: "30px" },
}: ChatAvatarsProps) => {
  const users = useMemo(() => userIds.filter(Boolean), [userIds]);

  const gridItems = useMemo(() => {
    if (users.length <= 1)
      return [] as Array<{ type: "avatar" | "counter"; value: string }>;

    if (users.length > 4) {
      return [
        { type: "avatar" as const, value: users[0] },
        { type: "avatar" as const, value: users[1] },
        { type: "avatar" as const, value: users[2] },
        { type: "counter" as const, value: `+${users.length - 3}` },
      ];
    }

    const firstFour = users.slice(0, 4).map((userId) => ({
      type: "avatar" as const,
      value: userId,
    }));

    while (firstFour.length < 4) {
      firstFour.push({ type: "avatar", value: "" });
    }

    return firstFour;
  }, [users]);

  if (!users.length) {
    return <div className={s.container} />;
  }

  if (users.length === 1) {
    return (
      <div className={s.container}>
        <div className={s.singleCell}>
          <CircleAvatar
            userId={users[0]}
            width={avatarsSize.oneAvtaar}
            height={avatarsSize.oneAvtaar}
            marginRight="0"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.grid}>
        {gridItems.map((item, index) => (
          <div
            className={`${s.cell} ${item.type === "counter" ? s.counterCell : ""}`}
            key={`${item.type}-${item.value}-${index}`}
          >
            {item.value ? (
              item.type === "counter" ? (
                <>
                  <span className={s.counterText}>{item.value}</span>
                </>
              ) : (
                <CircleAvatar
                  userId={item.value}
                  width={avatarsSize.multipleAvatars}
                  height={avatarsSize.multipleAvatars}
                  marginRight="0"
                  fontSize="12px"
                />
              )
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(ChatAvatars);
